/* eslint-disable @typescript-eslint/no-explicit-any */

import { useTranslation } from '@/i18n/client';
import { useStores } from '@/models';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import { getCheckoutInfo, getPayNowInfo } from '@/apis/checkout/checkout.api';
import {
  PAY_RESULT_SPECIAL_ORDER_TYPE_ENUM,
  PAYMENT_MODAL_TYPE_ENUM,
} from '@/apis/checkout/constants/constants';
import to from 'await-to-js';

import { DialogActions, DialogContent, DialogTitle } from '@mui/material';

import UKDialog from '@/components/UKDialog/index';
import UKDrawer from '@/components/UKDrawer';
import UKMaskLoading from '@/components/UKMaskLoading/index';
import UKToast from '@/components/UKToast/index';

import PaymentListCard from './paymentListCard';
import RepayButtons from './repayButtons';
import SafetyLoading from './safetyLoading';

import { sgmBizTrackSubmit } from '@/apis/checkout/utils/sgmTrack';
import JDIPrice from '@/components/price';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/26172';
import {
  SGM_TRACK_MAIN_TYPE,
  SGM_TRACK_TYPE,
} from '@/utils/logger/instance/checkoutBusinessLogger';
import { gtagPurchaseLog } from '@/utils/thirdParty/gtag';
import Styles from './repayOrderModal.module.scss';

interface RepayOrderModalProps {
  // 订单id, 必填
  orderId: string;
  // 控制组件显示/隐藏，必填
  open: boolean;
  // 关闭弹窗， 回调函数
  onClose: () => void;
  // 支付完成回调
  onFresh?: () => void;
  //二次支付样式，是Dialog还是Drawer PAYMENT_MODAL_TYPE_ENUM.MODAL | PAYMENT_MODAL_TYPE_ENUM.DRAWER
  paymentStyleType?: PAYMENT_MODAL_TYPE_ENUM;
  // 是否是购买会员支付 PAY_RESULT_SPECIAL_ORDER_TYPE_ENUM.PURCHASE_MEMBER
  specialOrderType?: PAY_RESULT_SPECIAL_ORDER_TYPE_ENUM;
}

interface PaymentMapType {
  [key: string]: string;
}

interface orderDetailType {
  unit: string;
  price: string;
  savedPrice: string;
}

const RepayOrderModal = observer((props: RepayOrderModalProps) => {
  const { onClose, paymentStyleType = PAYMENT_MODAL_TYPE_ENUM.MODAL } = props;
  const router = useRouter();
  const { checkoutStore } = useStores();
  const { creditCardStore, browserInfo, paymentStore } = checkoutStore;
  const { paymentList } = paymentStore;
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [orderPriceDetail, setOrderPriceDetail] = useState<any>({
    price: '',
    savedPrice: '',
    unit: '',
  });

  const initData = async (params?: {
    disabledPayMethods: string;
    currentUserPayMethods: string;
  }) => {
    UKMaskLoading.show();
    const requestData = {
      orderId: props.orderId,
      buId: '405',
      extParam: params ? { ...params } : undefined,
    };
    // 获取订单支付信息：支付方式列表
    const res = await getPayNowInfo(requestData);

    // Set currency && language
    checkoutStore.setLocalInfo(res?.localInfo || {});

    checkoutStore.setSafetyLoading(false);
    UKMaskLoading.close();
    setOrderPriceDetail({
      ...orderPriceDetail,
      ...res.orderInfo,
    });
    if (res?.paymentList?.length > 0) {
      paymentStore.setPaymentList(res.paymentList);
      return;
    }

    // SGM错误日志上报
    sgmBizTrackSubmit(
      {
        type: SGM_TRACK_MAIN_TYPE.CHECKOUT_API_ERROR,
        detailCode: SGM_TRACK_TYPE.REPAY_ORDER_CLIENT_NO_PAYMENTS,
      },
      {
        message: {
          traceId: res.traceId,
          code: res.code,
        },
      },
    );
    onClose();
    UKToast.show({
      content: t('common.message.networkError') as any, // 弹窗内容
      icon: 'error', // 可选：success, fail, loading, warn, info，影响图标
    });
  };

  // 支付完成 如果有回调onFresh，则执行onFresh；否则走通用逻辑，跳转finish页面
  const handlePayFinish = () => {
    setTimeout(() => {
      onClose();
      if (props.onFresh) {
        gtagPurchaseLog(props.orderId, orderPriceDetail.price);

        props.onFresh();
      } else {
        window.location.replace(
          `/checkout/finish/${paymentStore.payResult.id}/PC/${paymentStore.payResult.orderId}`,
        );
      }
    }, 2000);
  };

  // // 去订单列表
  // const toOrderList = () => {
  //   window.location.replace("/profile/my-orders");
  // };

  // 支付失败
  const onSubmitError = () => {
    console.log('onSubmitError');
  };

  const getBillingAddress = async (requestData: any) => {
    // 获取账单地址、金额详情
    const [err, res] = await to(getCheckoutInfo({ ...requestData }));
    console.log(!err, res, 'getBillingAddress');
    // 订单应付金额
    checkoutStore.setOrderPaymentPrice(res?.orderPriceDetail.price || '');

    if (!err && res) {
      creditCardStore.setBillingAddress(res.billingAddress);
    }
  };
  const initBrowserInfo = () => {
    const info: any = { ...browserInfo };
    info.screenWidth = screen.width;
    info.screenHeight = screen.height;
    info.userAgent = navigator.userAgent;
    info.timeZoneOffset = String(new Date().getTimezoneOffset());
    checkoutStore.setBrowserInfo({ ...info });
  };
  useEffect(() => {
    checkoutStore.setIsClient(true);
    paymentStore.setPaymentList([]);
    initData({
      disabledPayMethods: paymentStore.disabledPayMethods.join(','),
      currentUserPayMethods: '',
    });
    initBrowserInfo();
    const maskLoadingElement = document.getElementById('loading-mask');
    maskLoadingElement?.setAttribute('style', 'z-index: 1000000');
  }, []);

  useEffect(() => {
    checkoutStore.setRepayOrderId(props.orderId);

    if (props.orderId) {
      const requestData = {
        orderId: props.orderId,
      };
      // 获取账单地址
      getBillingAddress(requestData);
    }
  }, [props.orderId]);

  useEffect(() => {
    setIsOpen(props.open);
    // 初始化信用卡表单
    creditCardStore.initCreditForm();
    checkoutStore.setSafetyLoading(false);
  }, [props.open]);

  // 支付方式切换点击埋点
  const paymentTrackAction = (params: { payWay: string; channelName: string }) => {
    sendClickTrack.Cashier_Pop_PayWay({ ...params, orderId: props.orderId });
  };

  console.log(orderPriceDetail, 'orderPriceDetail');

  // 弹窗底部价格信息：实付价格+优惠金额
  const PriceDetailInfoRender = () => {
    // 有实付价格和货币符号，则展示Order total信息
    return orderPriceDetail.unit && orderPriceDetail.price ? (
      <div className={Styles.priceInfoBox}>
        <div className={Styles.leftBox}>{t('checkout.paymentDetail.orderTotal')}</div>
        <div className={Styles.rightBox}>
          {/* 有优惠金额，则展示Saved信息 */}
          {orderPriceDetail.savedPrice && orderPriceDetail.savedPrice !== '0.00' && (
            <div className={Styles.savedBox}>
              {t('checkout.paymentDetail.saved')}
              <JDIPrice
                symbol={orderPriceDetail.unit}
                price={orderPriceDetail.savedPrice}
                uiType={2}
                containerStyle={{
                  fontSize: '14px',
                  marginLeft: '4px',
                  lineHeight: '18px',
                  color: '#CC0C1C',
                  display: 'flex',
                  alignItems: 'center',
                }}
              />
            </div>
          )}
          <JDIPrice
            symbol={orderPriceDetail.unit}
            price={orderPriceDetail.price}
            uiType={2}
            containerStyle={{
              fontSize: '18px',
              marginLeft: '8px',
              lineHeight: '24px',
              height: '24px',
              color: '#1A1A1A',
              display: 'flex',
              alignItems: 'center',
            }}
          />
        </div>
      </div>
    ) : null;
  };
  return (
    <>
      {(paymentList as any[]).length ? (
        <>
          {paymentStyleType === PAYMENT_MODAL_TYPE_ENUM.MODAL && (
            <UKDialog
              open={isOpen}
              onClose={() => {
                props.onClose();
              }}
              showCloseBtn={true}
              className={Styles.repayOrderModalPage}
              sx={{
                '& .MuiDialog-paper': {
                  width: '620px', // 设置自定义宽度
                  maxWidth: 'none', // 取消最大宽度限制
                  maxHeight: '600px',
                  minHeight: '300px',
                },
              }}
            >
              <DialogTitle className={Styles.repayOrderModalTitle}>
                {t('checkout.payMethod.title')}
              </DialogTitle>
              <DialogContent>
                <div
                  exptag={'exp|' + EXPOSURE_RECORD.Cashier_Pop_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                  data-exptag-json-param={JSON.stringify({ orderId: props.orderId || '-100' })}
                >
                  <PaymentListCard
                    isHideTitle={true}
                    className={Styles.repayOrderPaymentCard}
                    onTrack={paymentTrackAction}
                    onFresh={initData}
                    specialOrderType={props.specialOrderType}
                  />
                </div>
              </DialogContent>
              <DialogActions className={Styles.repayOrderModalFooterBox}>
                {/* 实付金额+优惠金额展示 */}
                <PriceDetailInfoRender />
                {/* 支付按钮 */}
                <div className={Styles.repayOrderModalFooter}>
                  <RepayButtons
                    onSubmitError={onSubmitError}
                    onFinish={handlePayFinish}
                    onFresh={initData}
                    onClose={props.onClose}
                    specialOrderType={props.specialOrderType}
                  />
                </div>
              </DialogActions>
            </UKDialog>
          )}

          {paymentStyleType === PAYMENT_MODAL_TYPE_ENUM.DRAWER && (
            <UKDrawer
              title={t('checkout.payMethod.title')}
              anchor={'right'}
              openDrawer={isOpen}
              handleClose={() => {
                props.onClose();
              }}
              className={Styles.repayOrderDrawerPage}
            >
              <div className={Styles.repayOrderDrawerMain}>
                <div
                  exptag={'exp|' + EXPOSURE_RECORD.Cashier_Pop_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                  data-exptag-json-param={JSON.stringify({ orderId: props.orderId || '-100' })}
                  className={Styles.repayOrderDrawerContent}
                >
                  <PaymentListCard
                    isHideTitle={true}
                    className={Styles.repayOrderPaymentCard}
                    onTrack={paymentTrackAction}
                    onFresh={initData}
                    paymentStyleType={paymentStyleType}
                    specialOrderType={props.specialOrderType}
                  />
                </div>

                <div className={Styles.repayOrderDrawerFooterBox}>
                  {/* 实付金额+优惠金额展示 */}
                  <PriceDetailInfoRender />

                  {/* 支付按钮 */}
                  <div className={Styles.repayOrderDrawerFooter}>
                    <RepayButtons
                      onSubmitError={onSubmitError}
                      onFinish={handlePayFinish}
                      onFresh={initData}
                      onClose={props.onClose}
                      specialOrderType={props.specialOrderType}
                    />
                  </div>
                </div>
              </div>
            </UKDrawer>
          )}
          {checkoutStore.safetyLoading && <SafetyLoading />}
        </>
      ) : null}
    </>
  );
});

export default RepayOrderModal;
